/**
 * LprVerificationDrawer — EP-85 P2-05
 *
 * Right-side drawer (desktop) / bottom sheet (mobile) for reviewing and
 * verifying LPR events.  Receives the full event object + ordered pending
 * id list from LprEventLogPage — does NOT fetch the event by id.
 *
 * Layout:
 *   Block 1 — Captured event (front/rear thumbs, plate, type, color, direction, captured_at)
 *   Block 2 — Candidate vehicle (fetched via useVehicle; side-by-side on desktop)
 *   Footer  — "Last verified by X at HH:mm" (when event.verified_by_name is set)
 *   Actions — Verify success / Verify failed (reasons checkboxes) / Leave unmapped
 *
 * Key behaviours:
 *   • ↑/↓ arrow keys → onNavigate('up'|'down') (no on-screen chevrons)
 *   • Escape / [×] → onClose
 *   • Retry thumbs button (only when thumb_fetch_status === 'failed')
 *   • On successful verify/unmapped → invalidate lpr list query + auto-advance
 */
import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import {
  ScanLine,
  CheckCircle,
  XCircle,
  Unlink,
  X,
  Loader2,
} from 'lucide-react'
import { Drawer } from '@/components/ui/Drawer'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Lightbox } from '@/components/ui/Lightbox'
import { AttributeReconciliationTable } from './AttributeReconciliationTable'
import { useVehicle, usePropertyVehicleSearch } from '@/hooks/useVehicles'
import { lprEventKeys } from '@/hooks/useLprEvents'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import { api } from '@/lib/api'
import type { LprEvent, LprEventDirection } from '@/types/parking'

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

export type VerificationDirection = 'up' | 'down'

export interface LprVerificationDrawerProps {
  /** The LPR event to display. The drawer does NOT fetch by id. */
  event: LprEvent
  /**
   * Ordered list of pending event ids — used to auto-advance after a successful
   * action.  The page keeps this list in sync with the current filter/page.
   */
  pendingEventIds: number[]
  onClose: () => void
  /**
   * Called when the user presses ↑ or ↓ (keyboard nav) or after a successful
   * mutation (auto-advance).  The page is responsible for updating the selected
   * event id.
   */
  onNavigate: (direction: VerificationDirection) => void
}

// ---------------------------------------------------------------------------
// Verification reasons
// ---------------------------------------------------------------------------

const VERIFICATION_REASONS = [
  'text', 'color', 'type',
  'blurry', 'occluded', 'dark', 'glare', 'no_plate', 'not_a_vehicle', 'partial', 'duplicate',
  'unknown',
] as const
type VerificationReason = (typeof VERIFICATION_REASONS)[number]

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const apiBase = import.meta.env.VITE_API_URL || ''

function thumbUrl(key: string | null | undefined): string | null {
  if (!key) return null
  return key.startsWith('/api/images/')
    ? `${apiBase}${key}`
    : `${apiBase}/api/images/${key}`
}

function photoUrl(key: string | null | undefined): string | null {
  if (!key) return null
  return key.startsWith('/api/images/')
    ? `${apiBase}${key}`
    : `${apiBase}/api/images/${key}`
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PhotoFrame({
  src,
  alt,
  placeholder,
  onZoom,
}: {
  src: string | null
  alt: string
  placeholder: string
  /** Open the full-screen viewer. Parent binds the image set + start index. */
  onZoom?: () => void
}) {
  if (!src) {
    return (
      <div
        className="w-full aspect-video bg-base-200 rounded-lg flex flex-col items-center justify-center gap-2"
        aria-label={placeholder}
        data-testid="photo-placeholder"
      >
        <ScanLine className="w-8 h-8 text-base-content/30" />
        <span className="text-xs text-base-content/40">{placeholder}</span>
      </div>
    )
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full aspect-video object-cover rounded-lg cursor-zoom-in"
      data-testid="vehicle-photo"
      role="button"
      tabIndex={0}
      onClick={() => onZoom?.()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onZoom?.()
        }
      }}
    />
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
      {children}
    </p>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function LprVerificationDrawer({
  event,
  pendingEventIds,
  onClose,
  onNavigate,
}: LprVerificationDrawerProps) {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const formatDateTime = useDateTimeFormatter()
  const queryClient = useQueryClient()

  // Action state machine — 'idle' | 'verify_success' | 'verify_failed' | 'leave_unmapped'
  const [actionMode, setActionMode] = useState<
    'idle' | 'verify_success' | 'verify_failed' | 'leave_unmapped'
  >('idle')
  const [failedReasons, setFailedReasons] = useState<Set<VerificationReason>>(new Set())
  const [verificationNote, setVerificationNote] = useState('')
  // Full-screen photo viewer — holds the image set + start index (so ←/→ page
  // through a gallery), cleared on close.
  const [zoom, setZoom] = useState<{ images: string[]; index: number } | null>(null)

  // Manual vehicle linking (no-candidate path)
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null)
  const [vehicleQuery, setVehicleQuery] = useState('')

  // Operator-confirmed direction (defaults to the ML-detected value; editable in
  // the reconciliation table once a vehicle is linked). Reset on event change.
  const [operatorDirection, setOperatorDirection] = useState<LprEventDirection>(event.direction)

  // Reset action state when event changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset of local drawer state on event change
    setActionMode('idle')
    setFailedReasons(new Set())
    setVerificationNote('')
    setSelectedVehicleId(null)
    setVehicleQuery('')
    setOperatorDirection(event.direction)
  }, [event.id, event.direction])

  // ---------------------------------------------------------------------------
  // Arrow key navigation
  // ---------------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        onNavigate('down')
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        onNavigate('up')
      }
    },
    [onNavigate],
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // ---------------------------------------------------------------------------
  // Candidate vehicle
  // ---------------------------------------------------------------------------

  // 300ms debounce of vehicleQuery for the property search
  const [debouncedVehicleQuery, setDebouncedVehicleQuery] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedVehicleQuery(vehicleQuery), 300)
    return () => clearTimeout(timer)
  }, [vehicleQuery])

  const { data: vehicleSearchData, isFetching: isSearching } = usePropertyVehicleSearch(
    event.property_id,
    debouncedVehicleQuery,
  )
  const vehicleSearchResults = (vehicleSearchData?.data ?? []).slice(0, 10)

  // When a vehicle is manually selected, use it as the effective candidate
  const effectiveVehicleId = event.vehicle_id ?? selectedVehicleId
  const { data: vehicle, isLoading: isVehicleLoading } = useVehicle(effectiveVehicleId ?? 0)

  // ---------------------------------------------------------------------------
  // Verify mutation (PATCH /api/lpr_events/:id/verify)
  // ---------------------------------------------------------------------------

  const verifyMutation = useMutation({
    mutationFn: async (body: {
      verification_status: 'verified_success' | 'verified_failed' | 'pending'
      verification_reasons: string[]
      verification_note: string
      vehicle_id?: number | null
      operator_direction?: LprEventDirection
    }) => {
      await api.patch(`/api/lpr_events/${event.id}/verify`, body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lprEventKeys.lists() })
      // Auto-advance to next pending event, or close if none remain
      if (pendingEventIds.length > 1) {
        onNavigate('down')
      } else {
        onClose()
      }
    },
  })

  // ---------------------------------------------------------------------------
  // Action handlers
  // ---------------------------------------------------------------------------

  function handleVerifySuccess() {
    verifyMutation.mutate({
      verification_status: 'verified_success',
      verification_reasons: [],
      verification_note: verificationNote.trim(),
      // When there is no server-matched candidate, include the manually selected vehicle
      ...(!hasCandidate && selectedVehicleId != null ? { vehicle_id: selectedVehicleId } : {}),
      // Always send operator_direction so the API can store the correction on every
      // resolve path (positive and negative alike). Defaults to the ML-detected
      // direction when the operator has not changed it.
      operator_direction: operatorDirection,
    })
  }

  function handleVerifyFailed() {
    if (failedReasons.size === 0) return
    verifyMutation.mutate({
      verification_status: 'verified_failed',
      verification_reasons: Array.from(failedReasons),
      verification_note: verificationNote.trim(),
      // Always include operator_direction — the API needs it to emit the negative
      // disposition record (vehicle_id will be absent/null on a reject).
      operator_direction: operatorDirection,
    })
  }

  function handleLeaveUnmapped() {
    verifyMutation.mutate({
      verification_status: 'pending',
      verification_reasons: [],
      verification_note: '',
      vehicle_id: null,
      // Always include operator_direction for negative disposition storage.
      operator_direction: operatorDirection,
    })
  }

  function toggleReason(reason: VerificationReason) {
    setFailedReasons((prev) => {
      const next = new Set(prev)
      if (next.has(reason)) {
        next.delete(reason)
      } else {
        next.add(reason)
      }
      return next
    })
  }

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const thumbFailed = event.thumb_fetch_status === 'failed'
  // Captured-event images. Prefer the forward-looking `thumb_keys` array (the
  // pipeline will deliver multiple frames per event); fall back to the legacy
  // front/rear keys so existing payloads keep rendering. Resolved to URLs and
  // de-duplicated, preserving order.
  const capturedThumbs: string[] = thumbFailed
    ? []
    : Array.from(
        new Set(
          (event.thumb_keys && event.thumb_keys.length > 0
            ? event.thumb_keys
            : [event.front_thumb_key, event.rear_thumb_key]
          )
            .map((k) => thumbUrl(k))
            .filter((u): u is string => !!u),
        ),
      )
  const hasCandidate = !!event.vehicle_id
  const hasAuditFooter = !!(event.verified_by_name && event.verified_at)
  const isMutating = verifyMutation.isPending

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('lpr.verification_drawer.title')}
      ariaLabel={t('lpr.verification_drawer.aria_label')}
      hideBackdrop
    >
      <div className="space-y-6">
        {/* ------------------------------------------------------------------ */}
        {/* Block 1 — Candidate vehicle (shown first — it's the match target)   */}
        {/* ------------------------------------------------------------------ */}
        {(hasCandidate || !!selectedVehicleId) && (
          <div>
            <SectionLabel>{t('lpr.verification_drawer.candidate_vehicle')}</SectionLabel>

            {isVehicleLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-base-content/40" />
              </div>
            ) : vehicle ? (
              <>
                {/* Registered photos side-by-side on desktop. The full-screen
                    viewer pages through whichever registered photos are present. */}
                {(() => {
                  const regFront = photoUrl(vehicle.front_photo_key)
                  const regRear = photoUrl(vehicle.rear_photo_key)
                  const regSet = [regFront, regRear].filter((u): u is string => !!u)
                  return (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <p className="text-xs text-base-content/50 mb-1">
                          {t('lpr.verification_drawer.registered_front')}
                        </p>
                        <PhotoFrame
                          src={regFront}
                          alt={t('lpr.verification_drawer.registered_photo_alt')}
                          placeholder={t('lpr.verification_drawer.no_registered_photo')}
                          onZoom={
                            regFront
                              ? () => setZoom({ images: regSet, index: regSet.indexOf(regFront) })
                              : undefined
                          }
                        />
                      </div>
                      <div>
                        <p className="text-xs text-base-content/50 mb-1">
                          {t('lpr.verification_drawer.registered_rear')}
                        </p>
                        <PhotoFrame
                          src={regRear}
                          alt={t('lpr.verification_drawer.registered_photo_alt')}
                          placeholder={t('lpr.verification_drawer.no_registered_photo')}
                          onZoom={
                            regRear
                              ? () => setZoom({ images: regSet, index: regSet.indexOf(regRear) })
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  )
                })()}

                {/* Attribute reconciliation: ML-detected vs registered ground truth.
                    Replaces the flat <dl>; last_seen_at stays as a row below. */}
                <AttributeReconciliationTable
                  mlAttrs={{
                    color: event.color,
                    vehicle_type: event.vehicle_type,
                    plate_text: event.plate_text,
                    direction: event.direction,
                  }}
                  registeredVehicle={vehicle}
                  direction={operatorDirection}
                  onDirectionChange={setOperatorDirection}
                />

                {/* last_seen_at — context row below the reconciliation table */}
                {vehicle.last_seen_at && (
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-2">
                    <dt className="text-base-content/50">
                      {t('lpr.verification_drawer.last_seen')}
                    </dt>
                    <dd className="text-xs">{formatDateTime(vehicle.last_seen_at)}</dd>
                  </dl>
                )}
              </>
            ) : (
              <p className="text-sm text-base-content/40">
                {t('lpr.verification_drawer.vehicle_not_found')}
              </p>
            )}
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Block 1b — Link a vehicle (only when no server-matched candidate)   */}
        {/* ------------------------------------------------------------------ */}
        {!hasCandidate && (
          <div>
            <SectionLabel>{t('lpr.verification_drawer.link_vehicle')}</SectionLabel>

            <Input
              type="text"
              className="w-full mb-2"
              size="sm"
              placeholder={t('lpr.verification_drawer.search_vehicle_placeholder')}
              value={vehicleQuery}
              onChange={(e) => setVehicleQuery(e.target.value)}
              data-testid="vehicle-search-input"
              aria-label={t('lpr.verification_drawer.search_vehicle_placeholder')}
            />

            {vehicleQuery.trim().length < 2 ? (
              <p className="text-xs text-base-content/40">
                {t('lpr.verification_drawer.search_min_chars')}
              </p>
            ) : isSearching ? (
              <div className="flex items-center gap-2 py-2">
                <Loader2
                  className="w-4 h-4 animate-spin text-base-content/40"
                  aria-label={t('lpr.verification_drawer.searching_vehicles')}
                />
              </div>
            ) : vehicleSearchResults.length === 0 ? (
              <p className="text-xs text-base-content/40">
                {t('lpr.verification_drawer.no_vehicles_found')}
              </p>
            ) : (
              <div className="max-h-[40vh] overflow-y-auto rounded-lg border border-base-200">
                {vehicleSearchResults.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-base-200 flex flex-col gap-0.5 border-b border-base-200 last:border-b-0"
                    data-testid={`vehicle-result-${v.id}`}
                    onClick={() => {
                      setSelectedVehicleId(v.id)
                      setVehicleQuery('')
                    }}
                  >
                    <span className="font-mono font-medium">{v.license_plate}</span>
                    <span className="text-base-content/50 text-xs">
                      {v.vehicle_type} {v.color ? `· ${v.color}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {selectedVehicleId != null && (
              <div className="mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedVehicleId(null)
                    // Collapse the reconciliation table and reset to the ML direction
                    setOperatorDirection(event.direction)
                  }}
                  data-testid="btn-clear-selection"
                >
                  {t('lpr.verification_drawer.clear_selection')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Block 2 — Captured event (single thumbnail — the pipeline emits one) */}
        {/* ------------------------------------------------------------------ */}
        <div>
          <SectionLabel>{t('lpr.verification_drawer.captured_event')}</SectionLabel>

          {/* Captured photos — one today, multiple frames per event in future.
              Single image renders full-width; ≥2 fall into a 2-col gallery. The
              viewer pages through the whole captured set. */}
          <div
            className={`mb-3 ${capturedThumbs.length > 1 ? 'grid grid-cols-2 gap-2' : ''}`}
          >
            {capturedThumbs.length === 0 ? (
              <div
                className="w-full aspect-video bg-base-200 rounded-lg flex flex-col items-center justify-center gap-2"
                aria-label={t('lpr.thumbnail_unavailable')}
                data-testid="thumb-placeholder"
              >
                <ScanLine className="w-8 h-8 text-base-content/30" />
              </div>
            ) : (
              capturedThumbs.map((src, i) => (
                <PhotoFrame
                  key={src}
                  src={src}
                  alt={t('lpr.thumbnail_alt')}
                  placeholder={t('lpr.thumbnail_unavailable')}
                  onZoom={() => setZoom({ images: capturedThumbs, index: i })}
                />
              ))
            )}
          </div>

          {/* Event details */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-base-content/50">{t('lpr.plate_text')}</dt>
            <dd className="font-mono font-medium min-w-0 break-words">{event.plate_text ?? '—'}</dd>

            <dt className="text-base-content/50">{t('lpr.verification_drawer.vehicle_type')}</dt>
            <dd>{event.vehicle_type ? t(`vehicles.types.${event.vehicle_type}`) : '—'}</dd>

            <dt className="text-base-content/50">{t('lpr.verification_drawer.color')}</dt>
            <dd>{event.color ?? '—'}</dd>

            <dt className="text-base-content/50">{t('lpr.direction')}</dt>
            <dd>
              <Badge variant={event.direction === 'entry' ? 'success' : 'warning'}>
                {t(`lpr.directions.${event.direction}`)}
              </Badge>
            </dd>

            <dt className="text-base-content/50">{t('lpr.captured_at')}</dt>
            <dd className="text-xs">{formatDateTime(event.captured_at)}</dd>
          </dl>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* Action area                                                          */}
        {/* ------------------------------------------------------------------ */}
        <div className="space-y-3">
          {actionMode === 'idle' && (
            <>
              <Button
                variant="success"
                size="sm"
                block
                loading={isMutating}
                disabled={!hasCandidate && !selectedVehicleId}
                leftIcon={<CheckCircle className="w-4 h-4" />}
                onClick={() => {
                  setActionMode('verify_success')
                  handleVerifySuccess()
                }}
                data-testid="btn-verify-success"
              >
                {!hasCandidate
                  ? t('lpr.verification_drawer.link_and_verify')
                  : t('lpr.verification_drawer.verify_success')}
              </Button>

              <Button
                variant="error"
                size="sm"
                block
                leftIcon={<XCircle className="w-4 h-4" />}
                onClick={() => setActionMode('verify_failed')}
                data-testid="btn-verify-failed"
              >
                {t('lpr.verification_drawer.verify_failed')}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                block
                leftIcon={<Unlink className="w-4 h-4" />}
                loading={isMutating}
                onClick={() => {
                  setActionMode('leave_unmapped')
                  handleLeaveUnmapped()
                }}
                data-testid="btn-leave-unmapped"
              >
                {t('lpr.verification_drawer.leave_unmapped')}
              </Button>
            </>
          )}

          {actionMode === 'verify_failed' && (
            <div className="space-y-3" data-testid="verify-failed-form">
              <p className="text-sm font-medium">
                {t('lpr.verification_drawer.select_reasons')}
              </p>

              {VERIFICATION_REASONS.map((reason) => (
                <Checkbox
                  key={reason}
                  size="sm"
                  data-testid={`reason-${reason}`}
                  checked={failedReasons.has(reason)}
                  onChange={() => toggleReason(reason)}
                  aria-label={t(`lpr.verification_drawer.reasons.${reason}`)}
                  label={t(`lpr.verification_drawer.reasons.${reason}`)}
                />
              ))}

              <Textarea
                textareaClassName="text-sm"
                placeholder={t('lpr.verification_drawer.note_placeholder')}
                value={verificationNote}
                onChange={(e) => setVerificationNote(e.target.value)}
                rows={2}
                aria-label={t('lpr.verification_drawer.note_placeholder')}
                data-testid="verification-note"
              />

              <div className="flex gap-2">
                <Button
                  variant="error"
                  size="sm"
                  block
                  loading={isMutating}
                  disabled={failedReasons.size === 0}
                  onClick={handleVerifyFailed}
                  data-testid="btn-submit-failed"
                >
                  {t('lpr.verification_drawer.submit_failed')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActionMode('idle')}
                  leftIcon={<X className="w-4 h-4" />}
                  data-testid="btn-cancel"
                >
                  {tc('actions.cancel')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* Footer — audit trail                                                 */}
        {/* ------------------------------------------------------------------ */}
        {hasAuditFooter && (
          <div
            className="border-t border-base-200 pt-3"
            data-testid="audit-footer"
          >
            <p className="text-xs text-base-content/40">
              {t('lpr.verification_drawer.last_verified_by', {
                name: event.verified_by_name,
                time: formatDateTime(event.verified_at),
              })}
            </p>
          </div>
        )}
      </div>

      {/* Full-screen photo viewer (portals to body, so nesting here is fine).
          Pages through the clicked gallery (captured set or registered set). */}
      {zoom && (
        <Lightbox
          images={zoom.images}
          index={zoom.index}
          onIndexChange={(i) => setZoom((z) => (z ? { ...z, index: i } : z))}
          onClose={() => setZoom(null)}
        />
      )}
    </Drawer>
  )
}
